import styles from './styles.module.scss';
import { Box, List, ListItem, Typography } from '@mui/material';
import { Trans } from 'next-i18next';
import Image, { StaticImageData } from 'next/image';
import clsx from 'clsx';
import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { isMobile } from '@/libs/isMobile';
import { LazyLottie, VideoAnimation } from '@/components';

export enum PreferenceItemType {
  Image = 'image',
  Animation = 'animation',
  Video = 'video',
}

type PreferenceItem = {
  title: string;
  description: {
    key: string;
  };
  Icon: FC;
  id: string;
  list?: string[];
};

export type PreferenceItemImage = PreferenceItem & {
  type: PreferenceItemType.Image;
  imageSrc: string | StaticImageData;
  imageStyle?: CSSProperties;
};

export type PreferenceItemAnimation = PreferenceItem & {
  type: PreferenceItemType.Animation;
  animationSrc: string;
  previewSrc: string | StaticImageData;
};

export type PreferenceItemVideo = PreferenceItem & {
  type: PreferenceItemType.Video;
  animationSrc: string;
  previewSrc: string;
};

type PreferenceItemProps = {
  item: PreferenceItemImage | PreferenceItemAnimation | PreferenceItemVideo;
};

export const PreferenceItem: FC<PreferenceItemProps> = ({ item }) => {
  const ref = useRef<HTMLElement>(null);

  const [isActive, setIsActive] = useState(item.id === 'infoRow1');

  useEffect(() => {
    const handleScroll = (): void => {
      if (item.id === 'infoRow1' || isMobile()) return;

      const rect = (ref.current as HTMLElement).getBoundingClientRect();
      if (rect.y - window.innerHeight / 2 <= 0) {
        if (!isActive) setIsActive(true);
      } else {
        setIsActive(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [item.id, isActive]);

  return (
    <Box className={clsx(styles.row, { [styles.active]: isActive })} ref={ref}>
      <Box className={styles.rowCircle}>
        <Box />
      </Box>
      <Box className={styles.rowData}>
        <Box className={styles.rowDataIcon}>
          <item.Icon />
        </Box>
        <Typography variant="h4" className={styles.rowDataTitle}>
          {item.title}
        </Typography>
        <Typography variant="body1" className={styles.rowDataText}>
          <Trans
            i18nKey={item.description.key}
            ns="ourData"
            components={{
              br: <br />,
            }}
          />
        </Typography>
        {item?.list && (
          <List className={styles.rowDataList}>
            {item.list.map((inner) => (
              <ListItem className={styles.rowDataListItem} key={inner}>
                {inner}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <Box className={styles.rowImage}>
        {item.type === PreferenceItemType.Image && (
          <Image
            src={item.imageSrc}
            width={385}
            height={385}
            alt=""
            loading="lazy"
            style={item.imageStyle}
          />
        )}
        {item.type === PreferenceItemType.Animation && (
          <LazyLottie
            dataSrc={item.animationSrc}
            previewSrc={item.previewSrc}
            previewProps={{ alt: '', loading: 'lazy' }}
            loop
          />
        )}
        {item.type === PreferenceItemType.Video && (
          <VideoAnimation src={item.animationSrc} poster={item.previewSrc} />
        )}
      </Box>
    </Box>
  );
};
